import { Card, CardBody, Button } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { UsersEntity } from '@bitecritic-api/src/users/users.zod';
import { UserAvatar } from '~/components/atoms/UserAvatar';
import { ROUTES } from '~/constants/routes.contants';
import { useUserFollowing } from '~/hooks/useUserFollowing';
import useUserContext from '~/hooks/useUserContext';
import { useLocale } from "~/hooks/useLocale";
import { ReadOnlyRating } from '~/components/molecules/ReadOnlyRating';
import { CountryFlag } from '~/components/atoms/CountryFlag';

interface UserProfileCardProps {
  user: UsersEntity;
  className?: string;
  showStats?: boolean;
  stats?: {
    reviewCount: number;
    averageRating: number;
  };
}

export function UserProfileCard({ user, className, showStats = true, stats }: UserProfileCardProps) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();
  const { isFollowing, handleFollowAction, isLoading } = useUserFollowing(user?.id);
  const isCurrentUser = currentUser?.id === user.id;
  const { navigateTo } = useLocale();

  const handleProfileClick = () => navigateTo(`${ROUTES.USERS}/${user.username}`);

  return (
    <Card className={className}>
      <CardBody className="bg-background border-secondary-100 border rounded-lg">
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <UserAvatar
              user={user}
              size="lg"
              className="cursor-pointer"
              onClick={handleProfileClick}
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3
                  className="text-lg font-semibold cursor-pointer hover:text-warning-600"
                  onClick={handleProfileClick}
                >
                  {user.username}
                </h3>
                {user.countryCode && (
                  <CountryFlag
                    countryCode={user.countryCode}
                    countryName={user.countryName}
                    size="sm"
                  />
                )}
              </div>
              <p className="text-secondary-600">
                {user.firstName} {user.lastName}
              </p>
              {stats && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-secondary-500">
                    ({stats.reviewCount})
                  </span>
                  <ReadOnlyRating rating={stats.averageRating} size="sm" />
                </div>
              )}
            </div>
            {isCurrentUser ? (
              <Button
                color="warning"
                variant="flat"
                size="sm"
                onPress={handleProfileClick}
              >
                {t('profile.view')}
              </Button>
            ) : (
              <Button
                color={isFollowing ? "secondary" : "warning"}
                variant={isFollowing ? "bordered" : "solid"}
                size="sm"
                onPress={handleFollowAction}
                isLoading={isLoading}
              >
                {isFollowing ? t('profile.unfollow') : t('profile.follow')}
              </Button>
            )}
          </div>

          {/* Stats */}
          {showStats && (
            <div className="flex justify-around border-t border-secondary-200 pt-4">
              <div className="text-center">
                <div className="font-semibold text-foreground">{user.followerCount}</div>
                <div className="text-sm text-secondary-600">{t('profile.followers')}</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-foreground">{user.followingCount}</div>
                <div className="text-sm text-secondary-600">{t('profile.following')}</div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-foreground">{user.reviewCount}</div>
                <div className="text-sm text-secondary-600">{t('common.reviews')}</div>
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
} 